import React, { useState } from "react";
import { api, apiError } from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { Layers, Save, X } from "lucide-react";

const COLORS = [
  "#FF3D7F",
  "#FFC400",
  "#00B894",
  "#0984E3",
  "#A29BFE",
  "#E17055",
  "#2D3436",
];

export default function GroupAssignDialog({
  open,
  onOpenChange,
  messageIds,
  existingName,
  existingColor,
  onSaved,
}) {
  const [name, setName] = useState("");
  const [color, setColor] = useState(COLORS[0]);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (open) {
      setName(existingName || "");
      setColor(existingColor || COLORS[0]);
    }
  }, [open, existingName, existingColor]);

  const save = async () => {
    if (!name.trim()) {
      toast.error("Dai un nome al gruppo");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/staff/groups/assign", {
        message_ids: messageIds,
        group_name: name.trim(),
        group_color: color,
      });
      toast.success(`Gruppo "${data.group_name}" creato (${data.count} contenuti)`);
      onSaved && onSaved(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="group-assign-dialog"
        className="max-w-md rounded-3xl"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
            <Layers className="h-5 w-5" /> Raggruppa contenuti
          </DialogTitle>
          <DialogDescription>
            {messageIds?.length} contenuti selezionati. Dai loro un nome e un
            colore per trovarli facilmente.
          </DialogDescription>
        </DialogHeader>

        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] mb-1.5 block">
            Nome del gruppo
          </label>
          <Input
            data-testid="group-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !saving) save();
            }}
            placeholder="Es: Tutorial balayage 3 parti"
            className="rounded-full border-[var(--mp-border)] h-10"
            autoFocus
          />
        </div>

        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] mb-1.5 block">
            Colore
          </label>
          <div className="flex flex-wrap gap-1.5">
            {COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                data-testid={`group-color-${c}`}
                className={`h-8 w-8 rounded-full border-2 transition-transform ${
                  color === c
                    ? "border-[var(--mp-ink)] scale-110"
                    : "border-transparent hover:border-[var(--mp-border)]"
                }`}
                style={{ backgroundColor: c }}
                aria-label={c}
              />
            ))}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-full border-[var(--mp-ink)]"
            data-testid="group-cancel"
          >
            <X className="h-4 w-4 mr-1" /> Annulla
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            data-testid="group-save"
            className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
          >
            <Save className="h-4 w-4 mr-1" />
            {saving ? "Salvo…" : "Salva gruppo"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
